import React, { useState, useEffect, useCallback } from 'react';
import { useCurrentSystem, useGateways } from '../context/SystemContext';
import GatewayCard from '../GatewayCard';
import GatewayEditor from '../GatewayEditor';
import ZoneEditor from '../ZoneEditor';
import SystemPageHeader from '../components/SystemPageHeader';
import ConfirmDialog from '../ConfirmDialog';
import { Plus, Router, Layers, Edit, Trash2 } from 'lucide-react';
import { doc, deleteDoc, updateDoc } from 'firebase/firestore';
import { firestore } from '../common/firebase';
import {
	getZonesFromSystem,
	zonesToSystemFields,
	resizeColorArray,
	defaultZoneName,
	formatZoneType,
	DEFAULT_ZONE_TYPE,
} from '../zoneUtils';

function ZoneRow({ zone, index, onEdit, onDelete }) {
	const displayName = zone.name || defaultZoneName(index);

	return (
		<div className="border border-gray-200 dark:border-gray-700 rounded-xl p-3 flex items-center justify-between gap-2 bg-white dark:bg-gray-900 hover:border-[#880e4f]/50 transition-all text-sm">
			<div className="min-w-0 flex-1">
				<div className="flex items-center gap-2 font-medium">
					<span className="text-xs text-gray-400 dark:text-gray-500 tabular-nums w-5 shrink-0">{index + 1}</span>
					<span className="truncate">{displayName}</span>
					<span className="text-[10px] px-1.5 py-0.5 bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 rounded uppercase shrink-0">
						{formatZoneType(zone.type)}
					</span>
				</div>
				{zone.description && (
					<div className="ml-7 text-xs text-gray-500 dark:text-gray-400 truncate" title={zone.description}>
						{zone.description}
					</div>
				)}
			</div>
			<div className="flex gap-1.5 shrink-0">
				<button
					onClick={() => onEdit(index)}
					className="p-1.5 text-gray-400 hover:text-[#880e4f] hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg"
				>
					<Edit size={15} />
				</button>
				<button
					onClick={() => onDelete(index)}
					className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-950 rounded-lg"
				>
					<Trash2 size={15} />
				</button>
			</div>
		</div>
	);
}

function GatewaysView() {
	const { currentSystem } = useCurrentSystem();
	const { gateways = [] } = useGateways(currentSystem?.uid, 50);
	const [editingGateway, setEditingGateway] = useState(null);
	const [zones, setZones] = useState([]);
	const [editingZoneIndex, setEditingZoneIndex] = useState(null);
	const [addingZone, setAddingZone] = useState(false);
	const [deleteZoneIndex, setDeleteZoneIndex] = useState(null);
	const [savingZones, setSavingZones] = useState(false);

	useEffect(() => {
		if (currentSystem) {
			setZones(getZonesFromSystem(currentSystem));
		}
	}, [currentSystem]);

	const persistZones = useCallback(async (newZones) => {
		if (!currentSystem?.uid) return;
		setSavingZones(true);
		try {
			const fields = zonesToSystemFields(newZones);
			const color = resizeColorArray(currentSystem.color, newZones.length);
			await updateDoc(doc(firestore, 'systems', currentSystem.uid), {
				...fields,
				color,
			});
			setZones(newZones);
		} finally {
			setSavingZones(false);
		}
	}, [currentSystem]);

	if (!currentSystem) {
		return (
			<div className="p-6 text-center text-gray-500 dark:text-gray-400">
				Select a system from the Systems menu to manage gateways and zones.
			</div>
		);
	}

	const doDeleteGateway = async (gateway) => {
		const id = gateway?.device_id || gateway?.uid;
		if (id) await deleteDoc(doc(firestore, 'gateways', id));
	};

	const handleSaveZone = async (values) => {
		const newZones = [...zones];
		if (addingZone) {
			newZones.push({ ...values, type: values.type || DEFAULT_ZONE_TYPE });
			setAddingZone(false);
		} else if (editingZoneIndex != null) {
			newZones[editingZoneIndex] = { ...values, type: values.type || DEFAULT_ZONE_TYPE };
			setEditingZoneIndex(null);
		}
		await persistZones(newZones);
	};

	const handleDeleteZone = async () => {
		if (deleteZoneIndex == null || zones.length <= 1) return;
		const newZones = zones.filter((_, i) => i !== deleteZoneIndex);
		setDeleteZoneIndex(null);
		await persistZones(newZones);
	};

	const editingZone = addingZone
		? { isNew: true, name: defaultZoneName(zones.length), description: '', type: DEFAULT_ZONE_TYPE }
		: editingZoneIndex != null
			? zones[editingZoneIndex]
			: null;

	return (
		<div className="p-4 sm:p-6 max-w-7xl mx-auto">
			<SystemPageHeader
				currentSystem={currentSystem}
				title="Setup"
				subtitle="Gateways and lighting zones for this system"
			/>

			{/* Zones */}
			<div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-2xl p-4 sm:p-5 shadow-sm mb-6">
				<div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
					<div>
						<h2 className="font-semibold text-lg flex items-center gap-2 text-gray-900 dark:text-gray-100">
							<Layers size={18} className="text-[#880e4f]" /> Zones
						</h2>
						<p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
							{zones.length} zone{zones.length !== 1 ? 's' : ''} configured
							{savingZones && ' · saving…'}
						</p>
					</div>
					<button
						onClick={() => setAddingZone(true)}
						className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800"
					>
						<Plus size={15} /> Add Zone
					</button>
				</div>
				<div className="space-y-2">
					{zones.map((zone, i) => (
						<ZoneRow
							key={i}
							zone={zone}
							index={i}
							onEdit={setEditingZoneIndex}
							onDelete={setDeleteZoneIndex}
						/>
					))}
				</div>
			</div>

			{/* Gateways */}
			<div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-2xl p-4 sm:p-5 shadow-sm">
				<div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
					<h2 className="font-semibold text-lg flex items-center gap-2 text-gray-900 dark:text-gray-100">
						<Router size={18} className="text-[#880e4f]" /> Gateways
					</h2>
					<button
						onClick={() => setEditingGateway({ name: 'New Gateway' })}
						className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800"
					>
						<Plus size={15} /> Add
					</button>
				</div>
				<div className="space-y-2">
					{gateways.length > 0 ? gateways.map((gw) => (
						<GatewayCard
							key={gw.device_id || gw.uid}
							gateway={gw}
							editGateway={setEditingGateway}
							deleteGateway={doDeleteGateway}
						/>
					)) : (
						<div className="text-sm text-gray-500 dark:text-gray-400 italic p-4 border border-dashed rounded-xl">No gateways configured yet.</div>
					)}
				</div>
			</div>

			{editingGateway && (
				<GatewayEditor
					open={!!editingGateway}
					gateway={editingGateway}
					doneEdit={() => setEditingGateway(null)}
					system={currentSystem}
				/>
			)}

			{editingZone && (
				<ZoneEditor
					zone={editingZone}
					index={addingZone ? zones.length : editingZoneIndex}
					open={!!editingZone}
					onClose={() => { setEditingZoneIndex(null); setAddingZone(false); }}
					onSave={handleSaveZone}
				/>
			)}

			{deleteZoneIndex != null && (
				<ConfirmDialog
					open={deleteZoneIndex != null}
					title="Remove Zone?"
					message={`Remove zone "${zones[deleteZoneIndex]?.name || defaultZoneName(deleteZoneIndex)}"? Scenes using this zone will need updating.`}
					yes_text="Remove"
					no_text="Cancel"
					on_yes={handleDeleteZone}
					on_no={() => setDeleteZoneIndex(null)}
				/>
			)}
		</div>
	);
}

export default GatewaysView;
